import { getPreviewInstance } from '@components/Navbar/DesktopNavbar.js';
import { IS_MOBILE, NAVBAR_WIDTH } from '@constants/global.js';
import {
  AiIcon,
  AuctionIcon,
  CanvasIcon,
  CollapsePreviewIcon,
  CommentIcon,
  ExpandPreviewIcon,
  FileIcon,
  ReportIcon,
} from '@icons/index.js';
import { resizingDrawer, resizingPreview } from '@signals/CommonSignals';
import {
  firstChunkComplete,
  focusedObjectChanged,
  objectFocused,
  sideBarTabChanged,
  viewerResize,
} from '@signals/viewer';
import DrawerState from '@states/DrawerState.js';
import PreviewState from '@states/Preview.js';
import UserState from '@states/User';
import { ActiveModelState } from '@states/Viewer';
import { UIButton, UIDiv, UIElement } from '@ui';
import { animateValue } from '@utils/AnimationUtil.js';
import {
  FULLSCREEN_PRESETS,
  FullscreenManager,
} from '@utils/FullscreenManager.js';
import { Resizer } from '@utils/threeViewer/Resizer.js';

class Drawer extends UIDiv {
  constructor() {
    super();
    this.setId('drawer-global');
    this.setClass('hidden !z-[1000]');
    if (IS_MOBILE()) {
      this.addClass('isApp');
    } else {
      this.setStyle('width', [`${DrawerState.drawer.originalWidth}px`]);
    }

    this._suppressToggleHide = false;
    this._startUp = true;

    this.resizer = null;
    this.expandButtonIcon = new ExpandPreviewIcon({ color: 'white', size: 20 });

    // Use shared fullscreen manager
    this.fullscreenManager = new FullscreenManager();

    this.#initializeLayout();
    this.#initializeEventListeners();

    if (!this.tabBar) {
      this.tabBar = this.#createTabBar();
      this.add(this.tabBar);
    } else {
      // Update existing tab bar
      this.updateTabBar();
    }

    this.toggleButton = this.#buildToggler();

    resizingPreview.add(width => {
      const availableWidth = window.innerWidth - width - NAVBAR_WIDTH();
      // if (availableWidth < DrawerState.drawer.minWidth) return this.animateClose();
      if (DrawerState.drawer.width > availableWidth) {
        this.updateFn(availableWidth);
      }
    });

    this.setSignals();
  }

  /**
   * COMPONENTS
   */

  #initializeLayout() {
    this.headerContainer = new UIDiv();
    this.headerContainer.setClass(
      'drawer-header gap-2 pt-4 px-4 hidden md:!flex items-center justify-end',
    );
    this.add(this.headerContainer);

    this.headerContainer.add(this.#createExpandButton());

    this.contentWrapper = new UIDiv();
    this.contentWrapper.addClass(
      'drawer-content-wrapper flex-1 bg-white relative md:pb-0 touch-pan-x touch-pan-y',
    );
    this.contentWrapper.setId('drawer-content-wrapper');
    this.add(this.contentWrapper);

    this.add(this.#createHandle());
  }

  #createExpandButton() {
    const button = new UIButton();
    button.setClass(
      'cursor-pointer p-1 rounded transition-colors hover:bg-black/10 sidebar-expand-button',
    );

    button.add(this.expandButtonIcon);
    button.onClick(() => this.#toggleExpand());

    // Store reference to button for icon updates
    this.expandButton = button;

    window.addEventListener('drawer:collapse', () => {
      if (DrawerState.drawer.isExpanded) button.triggerClick();
    });

    return button;
  }

  #toggleExpand() {
    const maxWidth = window.innerWidth - NAVBAR_WIDTH();
    if (!DrawerState.drawer.isExpanded) {
      if (PreviewState?.preview) {
        window.dispatchEvent(new CustomEvent('preview:hide'));
      }

      // Hide competing components when expanding to fullscreen
      this.fullscreenManager.hideComponents(FULLSCREEN_PRESETS.PREVIEW);

      // Hide the resizer in fullscreen mode
      this.fullscreenManager.hideResizer(this.resizer);

      // Update the button icon - clear and re-add
      this.expandButton.clear();
      this.expandButtonIcon = new CollapsePreviewIcon({
        color: 'white',
        size: 20,
      });
      this.expandButton.add(this.expandButtonIcon);

      animateValue(DrawerState.drawer.originalWidth, maxWidth, this.updateFn);
    } else {
      // Show hidden components when collapsing from fullscreen
      this.fullscreenManager.cleanup(this.resizer);

      // Update the button icon - clear and re-add
      this.expandButton.clear();
      this.expandButtonIcon = new ExpandPreviewIcon({
        color: 'white',
        size: 20,
      });
      this.expandButton.add(this.expandButtonIcon);

      animateValue(
        DrawerState.drawer.width,
        DrawerState.drawer.originalWidth,
        this.updateFn,
      );
    }
    DrawerState.toggleExpand();
  }

  #createHandle() {
    const handleContainer = new UIDiv();
    handleContainer.setClass(
      'flex md:hidden items-center justify-center w-full pt-4 pb-4 handle-cont',
    );
    handleContainer.setStyle('z-index', ['1000']);

    const handle = new UIDiv();
    handle.setId('drawer-handle');
    handle.setClass(
      'h-1.5 w-[45%] !p-0 rounded-full bg-[var(--o-hive-primary-60)]',
    );
    handleContainer.add(handle);

    this.#initializeHandleLogic(handleContainer);

    return handleContainer;
  }

  #buildToggler() {
    // Create the toggle button with proper collapse/expand functions
    const toggleButton = new UIButton();
    toggleButton.setId('drawer-toggle-button');
    toggleButton.setClass(
      `drawer-toggle-button ${IS_MOBILE() ? 'mobile-mode' : 'side-bar'}`,
    );

    // Add the icon container
    const toggleButtonIcon = new UIDiv();
    toggleButton.add(toggleButtonIcon);

    // Add click handler
    toggleButton.onClick(() => {
      this.#handleToggleClick();
    });

    return toggleButton;
  }

  #handleToggleClick() {
    this._startUp = false;
    const previousContent = DrawerState.getPreviousContent();

    // If previous content was projectslist, reset to default content instead
    const contentToShow =
      previousContent === 'projectslist'
        ? 'comments'
        : previousContent || 'comments';

    // // Use previous content if available, otherwise default to 'files'
    if (!IS_MOBILE()) {
      this.toggleButton.toggleClass('flip');
      if (this.toggleButton.hasClass('flip')) {
        this.animateOpen(contentToShow);
      } else {
        this.animateClose();
      }
    }
    this.setContent(contentToShow);
  }

  /**
   * FUNCTIONS
   */

  animateClose() {
    const startWidth = DrawerState.drawer.width;

    if (DrawerState.drawer.isExpanded) {
      this.#toggleExpand();
    }

    // Store the current content before closing
    const currentContent = DrawerState.drawer.currentContent;
    DrawerState.setPreviousContent(currentContent);

    animateValue(startWidth, 0, this.updateFn, () => {
      // DrawerState.setContent('none');
      DrawerState.setWidth(0);
      DrawerState.setOriginalWidth(DrawerState.drawer.minWidth);
      this.collapseSidebar();
    });
  }

  animateOpen(contentKey) {
    const isNone = contentKey === 'none' || contentKey === '';
    const keyName = isNone ? 'comments' : contentKey;
    // Only open if currently closed
    if (DrawerState.drawer.isOpen)
      return this.updateFn(DrawerState.drawer.originalWidth);
    const targetWidth = DrawerState.drawer.originalWidth || 600;

    // First set the content in the state
    DrawerState.setContent(keyName);

    animateValue(0, targetWidth, this.updateFn, () => {
      DrawerState.setOriginalWidth(targetWidth);
      // Force show the content
      this.setContent(keyName, true);
      DrawerState.toggleDrawer(true);
      // Add flip class to openDrawerBtn
      const openDrawerBtn = document.querySelector(
        '.drawer-toggle-button.side-bar',
      );
      if (openDrawerBtn) openDrawerBtn.classList.add('flip');
    });
  }

  updateFn = currentWidth => {
    const parsedWidth = parseInt(currentWidth, 10);
    this.setStyle('width', [`${parsedWidth}px`]);
    requestAnimationFrame(() => {
      DrawerState.setWidth(parsedWidth);
      DrawerState.updateMaxHeight();
      viewerResize.dispatch();
      resizingDrawer.dispatch(currentWidth);
    });
  };

  setContent(keyName, force) {
    const openingNewViewer =
      // Allow projects list drawer on mobile for shared projects, and allow closing (keyName === 'none')
      !window.location.pathname.includes('viewer') &&
      IS_MOBILE() &&
      keyName !== 'projectslist' &&
      keyName !== 'none';
    if (openingNewViewer) return;

    this.contentWrapper.getChildren().forEach(child => {
      const content = DrawerState.getContent(keyName);
      if (child.id === content?.dom.id) {
        const fileListContainer = child.querySelector('.file-list-container');
        if (fileListContainer) {
          fileListContainer.classList.remove('hidden');
        }
        child.classList.toggle('hidden', force ? false : null);
      } else child.classList.toggle('hidden', true);
    });

    if (force) {
      this.#toggleHide(false);
    } else if (keyName !== 'none') {
      const content = DrawerState.getContent(keyName);
      this.#toggleHide(content?.hasClass('hidden'));
    } else this.#toggleHide(true);

    // Hide/show tab bar based on content type on mobile
    if (IS_MOBILE() && this.tabBar) {
      const shouldHideTabBar =
        keyName === 'projectslist' || keyName === 'notifications';
      this.tabBar.toggleClass('hidden', shouldHideTabBar);
    }

    // Update tab indicator position
    if (keyName !== 'none' && this.tabButtons && this.tabIndicator) {
      const tabIndex = this.tabList.findIndex(tab => tab.key === keyName);
      if (tabIndex !== -1) {
        // Update active tab button
        Object.entries(this.tabButtons).forEach(([key, btn]) => {
          if (key === keyName) btn.addClass('active');
          else btn.removeClass('active');
        });
        // Update indicator position
        this.tabIndicator.setStyle('left', [
          `${(100 / this.tabList.length) * tabIndex}%`,
        ]);
        this.activeTab = keyName;
      }
    }
    sideBarTabChanged.dispatch(keyName);
    // Update the state with the new content
    DrawerState.setContent(keyName);
  }

  addContent(content, keyName) {
    // Remove old content with the same key if it exists (prevents duplicate DOM elements on mobile navigation)
    const existingContent = DrawerState.getContent(keyName);
    if (existingContent) {
      try {
        this.contentWrapper.remove(existingContent);
      } catch (e) {
        // Content might not be in wrapper anymore, ignore
      }
    }

    this.contentWrapper.add(content);
    DrawerState.addContent(content, keyName);

    return DrawerState.getContent(keyName);
  }

  #toggleHide(_bool) {
    if (IS_MOBILE() && this._suppressToggleHide && _bool === false) {
      return;
    }
    DrawerState.toggleDrawer(!_bool);
    this.toggleClass('hidden', _bool);
    this.toggleClass('!z-[1000]', _bool);
  }

  #initializeHandleLogic(headerContainer) {
    const drawer = new UIElement(this.dom);
    const innerContent = new UIElement(this.contentWrapper.dom);
    if (!drawer.dom) return;

    let startY = 0;
    let currentHeight = 0;
    const minHeight = 100;

    headerContainer.onTouchStart(event => {
      startY = event.touches[0].clientY;
      currentHeight = drawer.dom.getBoundingClientRect().height;
      drawer.setStyle('transition', 'none');
    });

    headerContainer.onTouchMove(event => {
      const currentY = event.touches[0].clientY;
      const deltaY = startY - currentY;
      let newHeight = currentHeight + deltaY;
      newHeight = Math.max(
        minHeight,
        Math.min(DrawerState.drawer.maxHeight, newHeight),
      );
      drawer.dom.style.height = `${newHeight}px`;
    });

    headerContainer.onTouchEnd(() => {
      drawer.setStyle('transition', ['height 0.3s']);
      const currentHeight = drawer.dom.getBoundingClientRect().height;
      const closestSnapPoint = Math.min(
        DrawerState.drawer.snapPoints.reduce((prev, curr) => {
          return Math.abs(curr - currentHeight) < Math.abs(prev - currentHeight)
            ? curr
            : prev;
        }),
        DrawerState.drawer.maxHeight,
      );
      drawer.setHeight(`${closestSnapPoint}px`);
      if (closestSnapPoint === minHeight) {
        drawer.setStyle('height', ['0px']);
        innerContent.setStyle('height', ['0px']);
        setTimeout(() => {
          this.collapseSidebar();
          DrawerState.setContent('none');
          drawer.setStyle('height', ['revert-layer']);
          if (this.toggleButton) this.toggleButton.setDisplay('block');
        }, 200);
      }
    });
  }

  #getElemByClass = c => document.getElementsByClassName(c)[0];

  #openPanel = (elementToShow, force) => {
    this.setContent(elementToShow, force);
    Object.keys(DrawerState.drawer.selectedButtons).forEach(button => {
      const buttonElement = this.#getElemByClass(`${button}-btn`);
      if (buttonElement?.classList)
        buttonElement.classList?.toggle('selected', button === elementToShow);
      DrawerState.setSelectedButton(button, button === elementToShow);
    });

    // const updateHeight = currentHeight => {
    //   this.dom.style.height = `${currentHeight}px`;
    // };

    // for projects on mobile, animate height (Commented out: we dont have the project list in drawer anymore - keeping in case we revert it)
    // if (elementToShow === 'projectslist' && IS_MOBILE()) {
    //   const startHeight = this.dom.offsetHeight || 0;
    //   animateValue(0, DrawerState.drawer.maxHeight, updateHeight);
    // }

    animateValue(
      DrawerState.drawer.width,
      DrawerState.drawer.originalWidth,
      this.updateFn,
    );

    window.dispatchEvent(new CustomEvent('toolbar:close'));
  };

  #closePanel = () => {
    if (IS_MOBILE()) {
      DrawerState.setPreviousContent(DrawerState.drawer.currentContent);
    } else {
      animateValue(DrawerState.drawer.width, 0, this.updateFn);
    }
    this.setContent('none');

    // remove selected class from all buttons
    Object.keys(DrawerState.drawer.selectedButtons).forEach(button => {
      const buttonElement = this.#getElemByClass(`${button}-btn`);
      if (buttonElement?.classList)
        buttonElement.classList?.toggle('selected', false);
    });
    DrawerState.resetSelectedButtons();
  };

  collapseSidebar(elementToShow, force) {
    if (force) {
      this.#openPanel(elementToShow, true);
      if (elementToShow) {
        DrawerState.setPreviousContent(elementToShow);
      }
    } else if (!DrawerState.drawer.isOpen && elementToShow) {
      this.#openPanel(elementToShow);
      if (elementToShow) {
        DrawerState.setPreviousContent(elementToShow);
      }
    } else if (
      DrawerState.drawer.isOpen &&
      DrawerState.getPreviousContent() !== elementToShow &&
      DrawerState.getPreviousContent() &&
      elementToShow
    ) {
      this.#openPanel(elementToShow);
      if (elementToShow) {
        DrawerState.setPreviousContent(elementToShow);
      }
    } else {
      this.#closePanel();
    }

    const preview = getPreviewInstance();
    if (preview && preview.isFullScreen) {
      preview.shrink();
    }
  }

  /**
   * TABS
   */
  #getBaseTabs() {
    const isActiveModelGroup = ActiveModelState.activeModel[0]?.isObject3D;
    return [
      {
        icon: new AuctionIcon({ color: 'currentColor', size: 22 }),
        key: 'quotation',
        shouldShow: () => !!DrawerState.getContent('quotation'),
      },
      {
        icon: new FileIcon({ color: 'transparent', size: 22 }),
        shouldShow: () => !!DrawerState.getContent('files') && !!UserState.user,
        key: 'files',
      },
      {
        icon: new CommentIcon({ color: 'transparent', size: 22 }),
        key: 'comments',
        shouldShow: () => !!DrawerState.getContent('comments'),
      },
      {
        icon: new AiIcon({ color: 'currentColor', size: 22 }),
        key: 'ai-analyzer',
        shouldShow: () =>
          !!UserState.user && !!DrawerState.getContent('ai-analyzer'),
      },
      {
        icon: new CanvasIcon({ color: 'transparent', size: 22 }),
        key: 'canvas',
        shouldShow: () =>
          isActiveModelGroup && !!DrawerState.getContent('canvas'),
      },
      {
        icon: new ReportIcon({ color: 'transparent', size: 22 }),
        key: 'report',
        // shouldShow: () => !isActiveModelGroup && !!DrawerState.getContent('report'),
        shouldShow: () => false,
      },
    ];
  }

  #createTabBar() {
    const baseTabs = this.#getBaseTabs();
    // Filter tabs based on their conditions
    const visibleTabs = baseTabs.filter(tab => tab.shouldShow());
    // If no tabs are available, return empty structure
    if (visibleTabs.length === 0) {
      this.tabButtons = {};
      this.activeTab = null;
      this.tabList = [];
      return new UIDiv(); // Return empty div
    }

    const tabBar = new UIDiv();
    tabBar.setClass(
      'drawer-tab-bar flex flex-row items-center justify-between w-full px-2 pt-2 pb-0 relative -mb-0.5 pb-2.5 border-b border-gray-200',
    );
    this.tabButtons = {};
    this.activeTab = visibleTabs[0].key;

    // Indicator
    const indicator = new UIDiv();
    indicator.setClass(
      'drawer-tab-indicator absolute bottom-0 left-0 mb-0.5 h-0.5 bg-[var(--o-hive-primary-80)] transition-all duration-200',
    );
    indicator.setStyle('width', [`${100 / visibleTabs.length}%`]);
    tabBar.add(indicator);
    this.tabIndicator = indicator;

    visibleTabs.forEach((tab, idx) => {
      const btn = new UIDiv();
      btn.setClass(
        'relative z-1 drawer-tab-btn flex flex-col items-center justify-center flex-1 cursor-pointer py-1',
      );

      if (idx === 0) btn.addClass('active');

      btn.add(tab.icon);
      btn.onClick(() => this.#switchTab(tab.key, idx, visibleTabs.length));
      this.tabButtons[tab.key] = btn;
      tabBar.add(btn);
    });
    this.tabList = visibleTabs;
    return tabBar;
  }

  #switchTab(tabKey, idx, tabCount, suppressContentUpdate = false) {
    // Visual update
    Object.entries(this.tabButtons).forEach(([key, btn]) => {
      if (key === tabKey) btn.addClass('active');
      else btn.removeClass('active');
    });
    this.activeTab = tabKey;

    this.tabIndicator.setStyle('left', [`${(100 / tabCount) * idx}%`]);

    if (!suppressContentUpdate) {
      if (tabKey) {
        this.setContent(tabKey);
      } else {
        this.setContent('none');
      }
    }
  }

  updateTabBar() {
    if (!this.tabList) return;

    // Check if any content availability has changed that would affect tab visibility
    const baseTabs = this.#getBaseTabs();

    // Check if current tabs match what should be shown
    let shouldRecreate = false;

    // Check if any tab should be added or removed based on content
    for (const possibleTab of baseTabs) {
      const hasTab = this.tabList.some(tab => tab.key === possibleTab.key);
      const shouldHaveTab = possibleTab.shouldShow();

      if (hasTab !== shouldHaveTab) {
        shouldRecreate = true;
        break;
      }
    }

    if (shouldRecreate) {
      this.#recreateTabBar();
      return;
    }

    // Original logic for active model changes
    if (!ActiveModelState.activeModel[0]) return;

    // Check if we need to update the tab bar (only if file type changes)
    const currentLastTab = this.tabList[this.tabList.length - 1];
    const newLastTab =
      ActiveModelState.activeModel[0].type === 'Group'
        ? { icon: 'icons/brush', key: 'canvas' }
        : { icon: 'icons/report', key: 'report' };

    // Only update if the last tab type has changed
    if (currentLastTab && currentLastTab.key !== newLastTab.key) {
      this.#recreateTabBar();
    }
  }

  // Force recreation of tab bar (used when user state changes)
  #recreateTabBar() {
    const lastActiveTab = this.activeTab;
    // Remove old tab bar
    if (this.tabBar) {
      this.remove(this.tabBar);
    }
    // Create new tab bar with current state
    this.tabBar = this.#createTabBar();

    // Only add tab bar if there are tabs to show
    if (this.tabList && this.tabList.length > 0) {
      this.add(this.tabBar);

      // Restore active tab if it exists in new tab list, otherwise use first tab
      if (lastActiveTab && this.tabButtons[lastActiveTab]) {
        const tabIndex = this.tabList.findIndex(
          tab => tab.key === lastActiveTab,
        );
        if (tabIndex !== -1) {
          this.#switchTab(lastActiveTab, tabIndex, this.tabList.length, true);
        }
      } else {
        // If previous active tab no longer exists, activate first tab
        const firstTab = this.tabList[0];
        if (firstTab) {
          this.#switchTab(firstTab.key, 0, this.tabList.length, true);
        }
      }
    } else {
      // No tabs available, hide tab bar area
      this.tabBar = null;
      this.tabButtons = {};
      this.activeTab = null;
    }
  }

  /**
   * SIGNALS AND EVENTS
   */
  #initializeResizer() {
    // Ensure DrawerState width is synchronized with actual DOM width before initializing Resizer
    DrawerState.setWidth(this.dom.offsetWidth);

    this.resizer = new Resizer({
      position: 'left',
      minWidth: DrawerState.drawer.minWidth,
      maxWidth: window.innerWidth - NAVBAR_WIDTH(),
      useIndicators: true,
      dragThreshold: 40,
      onCollapse: () => this.animateClose(),
      onExpand: () => this.#toggleExpand(),
      getCurrentWidth: () => this.dom.offsetWidth,
      onResize: newWidth => this.updateFn(newWidth),
      appendTo: '#drawer-global',
      customStyles: 'max-md:hidden',
    });

    this.add(this.resizer);
  }

  #initializeEventListeners() {
    window.addEventListener('resize', () => {
      if (IS_MOBILE()) {
        this.toggleButton.removeClass('side-bar');
        this.resizer.addClass('hidden');
      } else {
        this.toggleButton.addClass('side-bar');
        this.resizer.removeClass('hidden');
      }
    });

    window.addEventListener('preview:expand', () => {
      if (DrawerState.drawer.isOpen) {
        // Explicitly set drawer as closed in state
        DrawerState.toggleDrawer(false);
        this.toggleButton.removeClass('flip');
      }
    });

    // Listen for model selection changes
    focusedObjectChanged.add(() => {
      if (ActiveModelState.activeModel[0]) {
        if (IS_MOBILE()) this._suppressToggleHide = true;
        this.updateTabBar();
        this._suppressToggleHide = false;
      }
    });

    // Listen for first item to be set
    objectFocused.add(object => {
      if (object) {
        ActiveModelState.setActiveModel([object]);
        if (IS_MOBILE()) this._suppressToggleHide = true;
        this.updateTabBar();
        this._suppressToggleHide = false;
      }
    });

    this.#initializeResizer();

    UserState.subscribe(() => {
      this.#recreateTabBar();
    });
  }

  setSignals() {
    firstChunkComplete.add(() => {
      this._startUp = false;
    });
  }
}

const MainDrawer = new Drawer();

export default MainDrawer;
